import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { ConsultationProvider } from "@/context/ConsultationContext";
import Layout from "@/components/Layout";
import AuthCallback from "@/components/AuthCallback";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Process from "@/pages/Process";
import Estimate from "@/pages/Estimate";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import AdminLogin from "@/pages/admin/Login";
import AdminDashboard from "@/pages/admin/Dashboard";

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash && !hash.includes("session_id=")) {
      const el = document.getElementById(hash.slice(1));
      if (el) { el.scrollIntoView({ behavior: "smooth" }); return; }
    }
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const NotFound = () => (
  <section className="flex min-h-[70vh] items-center bg-ink pt-20" data-testid="not-found-page">
    <div className="mx-auto max-w-xl px-5 text-center">
      <p className="font-display text-7xl text-gold">404</p>
      <h1 className="mt-4 font-display text-3xl text-offwhite">This page is still under construction.</h1>
      <Link to="/" data-testid="not-found-home-link" className="mt-8 inline-block rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-charcoal">
        Back to Home
      </Link>
    </div>
  </section>
);

function AppRouter() {
  const location = useLocation();
  // Detect OAuth return synchronously during render (fragment, not query) to avoid race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:slug" element={<ProjectDetail />} />
        <Route path="/process" element={<Process />} />
        <Route path="/estimate" element={<Estimate />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Legal kind="privacy" />} />
        <Route path="/terms" element={<Legal kind="terms" />} />
        <Route path="*" element={<NotFound />} />
      </Route>
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminDashboard />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ lerp: 0.09 });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  return (
    <LanguageProvider>
      <ConsultationProvider>
        <BrowserRouter>
          <ScrollManager />
          <AppRouter />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </ConsultationProvider>
    </LanguageProvider>
  );
}

export default App;
